import { Hash, User, UserRound, Car, MapPin, Clock, Plane, FileText } from "lucide-react";
import { StatusBadge, fmtDateTime, tzAbbr } from "@/components/portal/parts";

const TIMELINE_COLOR = {
  offer_received: "#D4A72C", accepted: "#34D399", chauffeur_assigned: "#60A5FA",
  driver_en_route: "#2DD4BF", driver_arrived: "#2DD4BF", passenger_on_board: "#2DD4BF",
  in_progress: "#2DD4BF", completed: "#38BDF8", canceled: "#F87171",
};

const prettyTrip = (t) => (t ? String(t).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

function Tag({ children }) {
  return <span className="rounded-full border border-gold/25 bg-gold/8 px-2.5 py-1 font-mono uppercase tracking-[0.14em] text-gold/80">{children}</span>;
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-gold/12 bg-obsidian-800 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold/80">
        {Icon && <Icon className="h-3.5 w-3.5" />} {title}
      </p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Facts({ rows }) {
  const items = rows.filter(Boolean);
  return (
    <dl className="space-y-2.5 text-sm">
      {items.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 border-b border-gold/6 pb-2 last:border-0">
          <dt className="text-ivory/45">{k}</dt>
          <dd className="text-right text-ivory/90">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// Premium read-only list card for the Corporate Reservations list + View-as-Client preview.
export function ReservationListCard({ r, onOpen, showCompany = false }) {
  return (
    <button
      onClick={() => onOpen(r.reservation_id)}
      data-testid={`reservation-card-${r.confirmation_number}`}
      className="group block w-full rounded-2xl border border-gold/12 bg-obsidian-800 p-5 text-left transition hover:border-gold/35 hover:shadow-[0_0_30px_rgba(212,175,55,0.08)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-gold/10 px-2.5 py-1 font-mono text-xs font-semibold text-gold">
          <Hash className="h-3 w-3" />{r.confirmation_number}
        </span>
        <StatusBadge status={r.status} />
      </div>
      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-gold/70">{fmtDateTime(r.pickup_datetime)}</p>
      <div className="mt-2 flex items-start gap-2.5 text-sm">
        <div className="mt-1 flex flex-col items-center">
          <span className="h-2 w-2 rounded-full bg-gold" />
          <span className="my-0.5 h-5 w-px bg-gradient-to-b from-gold/60 to-silver/30" />
          <span className="h-2 w-2 rounded-full border border-silver/60" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate text-ivory/90">{r.pickup_address || "—"}</p>
          <p className="truncate text-ivory/70">{r.dropoff_address || "—"}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gold/8 pt-3 text-xs text-ivory/55">
        {showCompany && <span className="inline-flex items-center gap-1.5 text-ivory/70">{r.company || "Unassigned"}</span>}
        {r.passenger_name && <span className="inline-flex items-center gap-1.5"><User className="h-3 w-3 text-gold/60" />{r.passenger_name}</span>}
        <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3 text-gold/60" />{r.chauffeur_name || "Chauffeur TBD"}</span>
        <span className="inline-flex items-center gap-1.5"><Car className="h-3 w-3 text-gold/60" />{r.vehicle || "Vehicle TBD"}</span>
        <span className="ml-auto text-gold/70 transition group-hover:translate-x-0.5">View detail →</span>
      </div>
    </button>
  );
}

// Premium read-only reservation detail. `showCompany` surfaces the corporate company (admin preview / ops).
export function ReservationDetailContent({ r, showCompany = false }) {
  const stops = Array.isArray(r.stops) ? r.stops : [];
  const route = [
    { label: "Pickup", address: r.pickup_address, time: r.pickup_datetime, kind: "pickup" },
    ...stops.map((s) => ({ label: "Stop", address: s.address || s.name || String(s), time: s.time || null, kind: "stop" })),
    { label: "Drop-off", address: r.dropoff_address, time: r.actual_completion_at || null, kind: "dropoff" },
  ];
  const company = showCompany ? r.company : (r.company || r.company_name);
  const assigned = ["driver_assigned", "driver_en_route", "driver_arrived", "passenger_on_board", "in_progress", "completed"].includes(r.status);
  return (
    <div className="space-y-5" data-testid="reservation-detail">
      {/* hero */}
      <div className="rounded-2xl border border-gold/20 bg-obsidian-900 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold/70">Reservation</p>
            <p className="mt-1 font-display text-3xl font-semibold text-ivory" data-testid="detail-confirmation">#{r.confirmation_number}</p>
          </div>
          <StatusBadge status={r.status} size="lg" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-[10px]">
          {r.trip_type && <Tag>{prettyTrip(r.trip_type)}</Tag>}
          {company && <Tag>{company}</Tag>}
        </div>
      </div>

      <Section title="Key Trip Details" icon={Clock}>
        <Facts rows={[
          ["Pickup", fmtDateTime(r.pickup_datetime)],
          r.passenger_name ? ["Passenger", r.passenger_name] : null,
          r.trip_type ? ["Service type", prettyTrip(r.trip_type)] : null,
          r.vehicle ? ["Vehicle category", r.vehicle] : null,
          r.booking_account_number ? ["Booking account", r.booking_account_number] : null,
        ]} />
      </Section>

      <Section title="Itinerary Route" icon={MapPin}>
        <ol>
          {route.map((p, i) => (
            <li key={i} className="flex gap-3" data-testid={`itinerary-${p.kind}`}>
              <div className="flex flex-col items-center">
                <span className={`mt-1 h-3 w-3 rounded-full ${p.kind === "pickup" ? "bg-gold" : p.kind === "dropoff" ? "border border-silver/70" : "bg-silver/60"}`} />
                {i < route.length - 1 && <span className="my-0.5 h-8 w-px bg-gradient-to-b from-gold/50 to-silver/20" />}
              </div>
              <div className="pb-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-gold/70">{p.label}{p.time ? ` · ${fmtDateTime(p.time)}` : ""}</p>
                <p className="mt-0.5 text-sm text-ivory/90">{p.address || "—"}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Assigned Chauffeur & Vehicle" icon={UserRound}>
        <Facts rows={[
          ["Chauffeur", r.chauffeur_name || (assigned ? "Assigned in Limo Anywhere — details not synced" : "To be assigned")],
          ["Vehicle", r.vehicle || (assigned ? "Assigned in Limo Anywhere — details not synced" : "To be assigned")],
        ]} />
        {assigned && (!r.chauffeur_name || !r.vehicle) && (
          <p className="mt-3 text-[11px] text-ivory/40" data-testid="assignment-note">
            This trip is assigned in Limo Anywhere, but the chauffeur/vehicle details were not included in the sync. They appear here automatically once Limo Anywhere sends them.
          </p>
        )}
      </Section>

      {r.flight && (
        <Section title="Flight Information" icon={Plane}>
          <Facts rows={Object.entries(r.flight).map(([k, v]) => [k.replace(/_/g, " "), String(v)])} />
        </Section>
      )}

      {r.trip_notes && (
        <Section title="Trip Notes" icon={FileText}>
          <p className="whitespace-pre-line text-sm text-ivory/80">{r.trip_notes}</p>
        </Section>
      )}

      <Section title="Reservation History" icon={Clock}>
        <ol className="space-y-3" data-testid="reservation-timeline">
          {(r.timeline || []).length === 0 && <li className="text-xs text-ivory/45" data-testid="timeline-empty">No verified events yet.</li>}
          {(r.timeline || []).map((e, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`timeline-event-${e.key}`}>
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: TIMELINE_COLOR[e.key] || "#C9A227" }} />
              <div><p className="text-sm text-ivory">{e.label}</p><p className="text-xs text-ivory/45">{fmtDateTime(e.at)}</p></div>
            </li>
          ))}
        </ol>
        <p className="mt-4 border-t border-gold/8 pt-3 text-[11px] text-ivory/40" data-testid="detail-last-sync">
          Last synchronized {fmtDateTime(r.last_synced_at || r.source_updated_at)} · times in {tzAbbr()} · read-only (managed in Limo Anywhere)
        </p>
      </Section>
    </div>
  );
}

// Slide-over drawer wrapper that hosts the premium detail content.
export function ReservationDetailDrawer({ r, onClose, showCompany = false }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60" onClick={onClose} data-testid="reservation-drawer">
      <div className="relative h-full w-full max-w-lg overflow-y-auto border-l border-gold/20 bg-obsidian-900 p-6 sm:p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="drawer-close" className="absolute right-5 top-5 z-10 rounded-full border border-gold/25 bg-obsidian-800 p-2 text-ivory/70 hover:text-ivory">✕</button>
        <ReservationDetailContent r={r} showCompany={showCompany} />
      </div>
    </div>
  );
}
